import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';

import { PenUnderlineIcon, StationIcon } from '@mopla/ui';

import { IStop } from '../types';

interface IEntryStopContentProps {
	/** Selected entry stop, or undefined when none is chosen yet. */
	stop?: IStop;
	/** Opens the stop search sheet. */
	onClick: () => void;
}

/**
 * Tappable entry-stop (Einstiegshaltestelle) row.
 * Empty state shows a red "enter stop" prompt; selected state shows the
 * station icon + stop name. Tapping anywhere opens the search sheet.
 */
export const EntryStopContent: FC<IEntryStopContentProps> = ({
	stop,
	onClick,
}) => {
	const { t } = useTranslation('ticketshop');

	return (
		<ButtonBase
			onClick={onClick}
			data-testid="entry-stop-row"
			sx={{
				width: '100%',
				justifyContent: 'flex-start',
				textAlign: 'left',
				py: '9px',
			}}
		>
			<Stack direction="row" alignItems="center" gap={1} sx={{ width: '100%' }}>
				<Box sx={{ color: 'primary.main', display: 'flex', flexShrink: 0 }}>
					{stop ? <StationIcon /> : <PenUnderlineIcon />}
				</Box>
				{stop ? (
					<Stack flex={1} minWidth={0}>
						<Typography variant="b2" color="primary.main" noWrap>
							{stop.name}
						</Typography>
						{stop.address && (
							<Typography variant="l1" color="solutions.600" noWrap>
								{stop.address}
							</Typography>
						)}
					</Stack>
				) : (
					<Typography variant="l1" color="error.main" sx={{ flex: 1 }}>
						{t('ticket_detail.stop_search.empty_prompt')}
					</Typography>
				)}
			</Stack>
		</ButtonBase>
	);
};
